import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import './StatisticsChart.css';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

interface StatisticsRowData {
  label: string;
  mer: number;
  mar: number;
  lun: number;
  dom: number;
  sab: number;
  ven: number;
  gio: number;
}

interface StatisticsChartProps {
  data: StatisticsRowData[];
  title: string;
  metricsToShow: string[]; // array di metriche da mostrare
  compareData?: StatisticsRowData[]; // dati anno precedente (opzionale)
  showComparison?: boolean; // se mostrare il confronto
  isWeekly?: boolean; // se è vista settimanale
}

const StatisticsChart: React.FC<StatisticsChartProps> = ({ 
  data, 
  title, 
  metricsToShow, 
  compareData, 
  showComparison = false,
  isWeekly = false 
}) => {
  // Colori per le diverse metriche
  const metricColors = {
    'Accessi': 'rgb(102, 126, 234)',
    'Registrazioni': 'rgb(34, 197, 94)',
    'Diete Nuove': 'rgb(239, 68, 68)',
    'Diete Sett': 'rgb(245, 158, 11)',
    'Diete Gior': 'rgb(168, 85, 247)',
    'Conversioni': 'rgb(236, 72, 153)',
    'Alimenti': 'rgb(14, 165, 233)'
  };

  if (metricsToShow.length === 0) {
    return (
      <div className="chart-container">
        <p>Seleziona almeno una metrica da visualizzare</p>
      </div>
    );
  }

  // Prepara i dati per il grafico - labels diverse per vista giornaliera vs settimanale
  const labels = isWeekly 
    ? ['6 sett fa', '5 sett fa', '4 sett fa', '3 sett fa', '2 sett fa', 'Sett scorsa', 'Sett corrente']
    : ['6gg fa', '5gg fa', '4gg fa', '3gg fa', '2gg fa', 'Ieri', 'Oggi'];
  const datasets: any[] = [];

  // Per ogni metrica selezionata, crea un dataset
  metricsToShow.forEach((metricName, index) => {
    const metricData = data.find(row => row.label === metricName);
    if (!metricData) return;

    const currentValues = [metricData.gio, metricData.ven, metricData.sab, metricData.dom, metricData.lun, metricData.mar, metricData.mer];
    const baseColor = metricColors[metricName as keyof typeof metricColors] || `hsl(${index * 60}, 70%, 50%)`;

    datasets.push({
      label: `${metricName} (Corrente)`,
      data: currentValues,
      borderColor: baseColor,
      backgroundColor: baseColor.replace('rgb', 'rgba').replace(')', ', 0.1)'),
      borderWidth: 2,
      fill: false, // Non riempire quando ci sono più metriche
      tension: 0.4,
      pointBackgroundColor: baseColor,
      pointBorderColor: '#fff',
      pointBorderWidth: 2,
      pointRadius: 4,
      pointHoverRadius: 6,
    });

    // Aggiungi il dataset di confronto se abilitato
    if (showComparison && compareData) {
      const compareMetricData = compareData.find(row => row.label === metricName);
      if (compareMetricData) {
        const compareValues = [compareMetricData.gio, compareMetricData.ven, compareMetricData.sab, compareMetricData.dom, compareMetricData.lun, compareMetricData.mar, compareMetricData.mer];
        
        datasets.push({
          label: `${metricName} (Anno Precedente)`,
          data: compareValues,
          borderColor: baseColor.replace('rgb', 'rgba').replace(')', ', 0.5)'),
          backgroundColor: baseColor.replace('rgb', 'rgba').replace(')', ', 0.05)'),
          borderWidth: 2,
          fill: false,
          tension: 0.4,
          pointBackgroundColor: baseColor.replace('rgb', 'rgba').replace(')', ', 0.5)'),
          pointBorderColor: '#fff',
          pointBorderWidth: 2,
          pointRadius: 3,
          pointHoverRadius: 5,
          borderDash: [5, 5] as any,
        } as any);
      }
    }
  });

  const chartData = {
    labels,
    datasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 150, // Transizione molto rapida
    },
    plugins: {
      legend: {
        display: metricsToShow.length > 1 || !!(showComparison && compareData), // Mostra se ci sono più metriche o confronto
        position: 'top' as const,
        labels: {
          boxWidth: 12,
          padding: 15,
          font: {
            size: 11,
          },
          color: '#666',
        },
      },
      title: {
        display: true,
        text: title,
        font: {
          size: 16,
          weight: 'bold' as const,
        },
        color: '#333',
        padding: 20,
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          color: '#666',
          font: {
            size: 12,
          },
        },
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: '#666',
          font: {
            size: 12,
          },
        },
      },
    },
    elements: {
      point: {
        hoverBorderWidth: 3,
      },
    },
  };

  return (
    <div className="chart-container">
      <Line data={chartData} options={options} />
    </div>
  );
};

export default StatisticsChart;